import { MantineProvider, Tuple } from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import theme, { Colors } from "theme";

import { alpha } from "../../theme/helpers";

function colorTuple(colors: Colors | string[]): Tuple<string, 10> {
  if (Object.values(colors).length < 10)
    throw new Error("Custom colors must have at least 10 shades");
  const tupleColors = Object.values(colors);

  return [
    tupleColors[0],
    tupleColors[1],
    tupleColors[2],
    tupleColors[3],
    tupleColors[4],
    tupleColors[5],
    tupleColors[6],
    tupleColors[7],
    tupleColors[8],
    tupleColors[9],
  ];
}

const ThemeProvider = ({ children }: React.PropsWithChildren) => (
  <MantineProvider
    theme={{
      components: {
        Autocomplete: {
          defaultProps: {
            size: "md",
            variant: "unstyled",
          },
          sizes: {
            md: () => ({
              input: {
                height: theme.spacing[12],
              },
            }),
          },

          styles: () => ({
            input: {
              background: alpha(theme.colors.charade[9], 0.2),
              border: `1px solid ${theme.colors.shark[5]}`,
              borderRadius: theme.borderRadius.md,
              color: theme.colors.shark[0],
              fontSize: theme.fontSize.base,
              fontWeight: theme.fontWeight.regular,
              outline: "none",
              padding: `${theme.spacing[1]} ${theme.spacing[2]}`,
              "::placeholder, &[data-invalid]::placeholder": {
                color: theme.colors.shark[5],
              },
              "&:focus": {
                border: `1px solid ${theme.colors.shark[5]}`,
              },
              "&:active": {
                border: `1px solid ${theme.colors.shark[5]}`,
              },
            },
            label: {
              color: theme.colors.shark[2],
              fontSize: theme.fontSize.base,
              fontWeight: theme.fontWeight.regular,
            },
          }),
          variants: {
            "form-control": () => ({
              error: {
                gridColumnStart: 2,
                gridRowStart: 2,
              },
              root: {
                alignItems: "center",
                borderBottom: `1px solid ${theme.colors.shark[7]}`,
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                padding: `${theme.spacing["1.5"]} ${theme.spacing[4]}`,
              },
            }),
          },
        },

        Badge: {
          styles: () => ({
            root: {
              borderRadius: theme.borderRadius.md,
            },
          }),
          variants: {
            danger: () => ({
              root: {
                backgroundColor: theme.colors.roman[500],
                width: 80,
                height: 34,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "17px !important",
              },
              inner: {
                color: theme.colors.shark[8],
                textTransform: "uppercase",
                fontSize: theme.fontSize.xs1,
                letterSpacing: "0.06em",
                fontWeight: theme.fontWeight.semiBold,
                fontFamily: theme.fontFamily.montserrat,
              },
              leftSection: {
                svg: {
                  color: theme.colors.shark[8],
                  width: 11,
                  height: 11,
                  marginTop: 5,
                },
              },
            }),
            neutral: () => ({
              root: {
                backgroundColor: theme.colors.fuscousGray[350],
                width: 80,
                height: 34,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "17px !important",
              },
              inner: {
                color: theme.colors.shark[8],
                textTransform: "uppercase",
                fontSize: theme.fontSize.xs1,
                letterSpacing: "0.06em",
                fontWeight: theme.fontWeight.semiBold,
                fontFamily: theme.fontFamily.montserrat,
              },
              leftSection: {
                svg: {
                  color: theme.colors.shark[8],
                  width: 11,
                  height: 11,
                  marginTop: 5,
                },
              },
            }),
            concernCount: () => ({
              root: {
                backgroundColor: "#FCA082",
                color: theme.colors.black,
                fontWeight: theme.fontWeight.bold,
                fontSize: theme.fontSize.sm1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 5,
                minWidth: 19,
                borderRadius: 5,
              },
            }),
          },
        },

        Button: {
          sizes: {
            xs: () => ({
              label: {
                fontSize: theme.fontSize.xs1,
              },
              root: {
                padding: `${theme.spacing[0.5]} ${theme.spacing[3]}`,
              },
            }),
            sm: () => ({
              label: {
                fontSize: theme.fontSize.xs1,
              },
              root: {
                padding: `${theme.spacing[1.5]} ${theme.spacing[6]}`,
              },
            }),
            lg: () => ({
              label: {
                fontSize: theme.fontSize.base,
              },
              root: {
                padding: `${theme.spacing[4]} ${theme.spacing[12]}`,
              },
            }),
          },
          styles: () => ({
            label: {
              fontFamily: theme.fontFamily.montserrat,
              textTransform: "uppercase",
            },
            root: {
              borderRadius: theme.borderRadius.full,
              "&[data-loading]::before": {
                borderRadius: theme.borderRadius.full,
              },
            },
          }),
          variants: {
            primary: () => ({
              label: {
                color: theme.colors.shark[8],
                fontWeight: theme.fontWeight.semiBold,
              },
              root: {
                backgroundColor: theme.colors.lima[5],
                "&:hover": {
                  backgroundColor: theme.colors.lima[6],
                },
              },
            }),

            primaryDashed: () => ({
              root: {
                backgroundColor: theme.colors.transparent,
                border: `1px dashed ${theme.colors.grass[500]}`,
                color: theme.colors.shark[0],
                fontSize: theme.fontSize.xs,
                letterSpacing: "0.06em",
                fontWeight: theme.fontWeight.semiBold,
                textTransform: "uppercase",
                "& svg": {
                  color: theme.colors.shark[0],
                },
                "&:hover": {
                  backgroundColor: theme.colors.grass[500],
                  border: `1px solid ${theme.colors.grass[500]}`,
                  color: theme.colors.charade[9],
                  "& svg": {
                    color: theme.colors.charade[9],
                  },
                },
              },
            }),

            selected: () => ({
              label: {
                color: theme.colors.offWhite,
                fontSize: theme.fontSize.xs1,
                fontWeight: theme.fontWeight.semiBold,
              },
              root: {
                backgroundColor: theme.colors.blueChill[6],
                padding: "6px 12px",
                "&:hover": {
                  backgroundColor: theme.colors.blueChill[7],
                },
              },
            }),

            unselected: () => ({
              label: {
                color: theme.colors.offWhite,
                fontSize: theme.fontSize.xs1,
                fontWeight: theme.fontWeight.semiBold,
              },
              root: {
                backgroundColor: theme.colors.shark[6],
                padding: "6px 12px",
                "&:hover": {
                  backgroundColor: theme.colors.shark[7],
                },
              },
            }),

            link: () => ({
              label: {
                color: theme.colors.white,
                fontFamily: theme.fontFamily.montserrat,
              },
              root: {
                backgroundColor: theme.colors.transparent,
                border: "none",
                padding: 0,
                "&:disabled": {
                  backgroundColor: theme.colors.transparent,
                },
                "&:hover": {
                  backgroundColor: theme.colors.transparent,
                },
              },
            }),

            secondary: () => ({
              label: {
                color: theme.colors.shark[3],
                fontWeight: theme.fontWeight.medium,
              },
              root: {
                border: `1px solid ${theme.colors.shark[3]}`,
                "&:disabled": {
                  backgroundColor: "inherit",
                  borderColor: theme.colors.shark[4],
                  color: theme.colors.shark[4],
                },
                "&:hover": {
                  borderColor: theme.colors.shark[4],
                  ".mantine-Button-label": {
                    color: theme.colors.shark[4],
                  },
                },
                "&[data-loading]": {
                  borderColor: theme.colors.shark[4],
                  ".mantine-Button-label": {
                    color: theme.colors.shark[4],
                  },
                  "&::before": {
                    backgroundColor: "inherit",
                  },
                },
              },
            }),

            dashed: () => ({
              root: {
                backgroundColor: theme.colors.transparent,
                border: `1px dashed ${theme.colors.blue["200"]}`,
                color: theme.colors.gray["300"],
                fontSize: theme.fontSize.xs,
                lineHeight: "46px",
                letterSpacing: "0.06em",
                fontWeight: 600,
                height: "46px",
                textTrasform: "uppercase",
                padding: `0 ${theme.spacing[7]} 0 ${theme.spacing[5]}`,
                "&:disabled": {
                  backgroundColor: theme.colors.transparent,
                },
                "&:hover": {
                  backgroundColor: theme.colors.blue["200"],
                  color: theme.colors.blue["500"],
                },
              },
            }),

            confirmable_pending: () => ({
              root: {
                backgroundColor: theme.colors.transparent,
                border: `1px dashed ${theme.colors.green["500"]}`,
                color: theme.colors.gray["300"],
                fontSize: theme.fontSize.xs,
                lineHeight: "46px",
                letterSpacing: "0.06em",
                fontWeight: 600,
                height: "46px",
                textTrasform: "uppercase",
                padding: `0 ${theme.spacing[12]}`,
                "&:disabled": {
                  backgroundColor: theme.colors.transparent,
                },
                "&:hover": {
                  backgroundColor: theme.colors.green["500"],
                  color: theme.colors.blue["500"],
                },
              },
            }),

            confirmable_confirmed: () => ({
              root: {
                backgroundColor: theme.colors.transparent,
                border: `1px solid ${theme.colors.green["500"]}`,
                color: theme.colors.gray["300"],
                fontSize: theme.fontSize.xs,
                lineHeight: "46px",
                letterSpacing: "0.06em",
                fontWeight: 600,
                height: "46px",
                textTrasform: "uppercase",
                padding: `0 ${theme.spacing[12]}`,
                "&:disabled": {
                  backgroundColor: theme.colors.transparent,
                },
                "&:hover": {
                  backgroundColor: theme.colors.green["500"],
                  color: theme.colors.blue["500"],
                },
              },
            }),

            primaryOutline: () => ({
              root: {
                border: `1px solid ${theme.colors.lima[5]}`,
                color: theme.colors.shark[2],
                "&:hover": {
                  backgroundColor: theme.colors.lima[6],
                  border: `1px dashed ${theme.colors.lima[6]}`,
                  color: theme.colors.shark[8],
                },
              },
            }),

            unsure: () => ({
              label: {
                color: theme.colors.shark[3],
                padding: `0 ${theme.spacing[2]}`,
                fontSize: theme.fontSize.xs,
              },
              root: {
                border: `1px solid ${theme.colors.shark[3]}`,
                flexShrink: 0,
                width: "auto",
                "&:active": {
                  backgroundColor: theme.colors.blueChill[6],
                  border: `1px solid ${theme.colors.blueChill[6]}`,
                  ".mantine-Button-icon": {
                    display: "flex",
                  },
                },
              },
            }),

            unsureSelected: () => ({
              label: {
                color: theme.colors.shark[0],
                ".mantine-Button-icon": {
                  svg: {
                    color: theme.colors.shark[0],
                  },
                },
              },
              root: {
                backgroundColor: theme.colors.blueChill[6],
                border: `1px solid ${theme.colors.blueChill[6]}`,
                ".mantine-Button-icon": {
                  display: "flex",
                  svg: {
                    color: theme.colors.shark[0],
                  },
                },
              },
            }),
          },
        },

        Checkbox: {
          styles: () => ({
            input: {
              backgroundColor: theme.colors.transparent,
              borderColor: theme.colors.fuscousGray["300"],

              "&:disabled": {
                backgroundColor: theme.colors.transparent,
                borderColor: theme.colors.fuscousGray["300"],
              },

              "&:checked": {
                backgroundColor: theme.colors.transparent,
                borderColor: theme.colors.blueChill[6],
              },
            },

            label: {
              color: theme.colors.shark[0],
              fontSize: theme.fontSize.base,
              fontWeight: theme.fontWeight.regular,
            },

            root: {
              ".mantine-Checkbox-icon": {
                color: `${theme.colors.blueChill[6]} !important`,
              },
            },
          }),
          variants: {
            "form-control": () => ({
              root: {
                alignItems: "center",
                borderBottom: `1px solid ${theme.colors.shark[7]}`,
                padding: `${theme.spacing[2]} ${theme.spacing[4]}`,
              },
            }),
          },
        },

        Container: {
          defaultProps: {
            p: 0,
          },
        },

        DatePickerInput: {
          defaultProps: {
            size: "md",
            variant: "unstyled",
          },
          sizes: {
            md: () => ({
              input: {
                height: theme.spacing[12],
              },
            }),
          },
          styles: () => ({
            input: {
              background: theme.colors.transparent,
              border: `1px solid ${theme.colors.shark[5]}`,
              borderRadius: theme.borderRadius.md,
              color: theme.colors.shark[0],
              fontSize: theme.fontSize.base,
              fontWeight: theme.fontWeight.regular,
              "&:focus": {
                border: `1px solid ${theme.colors.shark[5]}`,
              },
            },
            label: {
              color: theme.colors.shark[2],
              fontSize: theme.fontSize.base,
              fontWeight: theme.fontWeight.regular,
            },
          }),
          variants: {
            "form-control": () => ({
              error: {
                gridColumnStart: 2,
                gridRowStart: 2,
              },
              input: {
                background: theme.colors.transparent,
                borderWidth: 0,
              },
              root: {
                alignItems: "center",
                borderBottom: `1px solid ${theme.colors.shark[7]}`,
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                padding: `${theme.spacing["1.5"]} ${theme.spacing[4]}`,
              },
            }),
          },
        },
        DateTimePicker: {
          defaultProps: {
            size: "md",
            variant: "unstyled",
          },
          sizes: {
            md: () => ({
              input: {
                height: theme.spacing[12],
              },
            }),
          },
          styles: () => ({
            input: {
              background: theme.colors.shark[7],
              border: `1px solid ${theme.colors.shark[5]}`,
              borderRadius: theme.borderRadius.md,
              color: theme.colors.shark[0],
              fontSize: theme.fontSize.base,
              fontWeight: theme.fontWeight.regular,
            },
            label: {
              color: theme.colors.shark[2],
              fontSize: theme.fontSize.base,
              fontWeight: theme.fontWeight.regular,
            },
          }),
          variants: {
            "form-control": () => ({
              error: {
                gridColumnStart: 2,
                gridRowStart: 2,
              },
              input: {
                background: theme.colors.transparent,
                borderWidth: 0,
              },
              root: {
                alignItems: "center",
                borderBottom: `1px solid ${theme.colors.shark[7]}`,
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                padding: `${theme.spacing["1.5"]} ${theme.spacing[4]}`,
              },
            }),
          },
        },
        DateInput: {
          defaultProps: {
            variant: "unstyled",
          },
          styles: () => ({
            input: {
              background: theme.colors.shark[7],
              border: `1px solid ${theme.colors.shark[5]}`,
              borderRadius: theme.borderRadius.md,
              color: theme.colors.shark[0],
              fontSize: theme.fontSize.base,
              fontWeight: theme.fontWeight.regular,
              height: theme.spacing[9],
              padding: `${theme.spacing[1]} ${theme.spacing[2]}`,
            },
            label: {
              color: theme.colors.shark[2],
              fontSize: theme.fontSize.base,
              fontWeight: theme.fontWeight.regular,
            },
          }),
          variants: {
            "form-control": () => ({
              input: {
                background: theme.colors.transparent,
                borderWidth: 0,
              },
              root: {
                alignItems: "center",
                borderBottom: `1px solid ${theme.colors.shark[7]}`,
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                padding: `${theme.spacing[2]} ${theme.spacing[4]}`,
              },
            }),
          },
        },

        MultiSelect: {
          defaultProps: {
            size: "md",
            variant: "unstyled",
          },
          sizes: {
            md: () => ({
              input: {
                minHeight: theme.spacing[12],
              },
            }),
          },
          styles: () => ({
            dropdown: {
              background: theme.colors.shark[0],
            },
            input: {
              background: alpha(theme.colors.charade[9], 0.2),
              border: `1px solid ${theme.colors.shark[5]}`,
              borderRadius: theme.borderRadius.md,
              color: theme.colors.shark[0],
              fontSize: theme.fontSize.base,
              fontWeight: theme.fontWeight.regular,
              outline: "none",
              padding: `${theme.spacing[1]} ${theme.spacing[2]}`,
              "&:focus": {
                border: `1px solid ${theme.colors.shark[5]}`,
              },
            },
            item: {
              "&[data-hovered]": {
                backgroundColor: theme.colors.shark[2],
              },
              "&[data-selected]": {
                backgroundColor: theme.colors.shark[5],
                "&:hover": {
                  backgroundColor: theme.colors.shark[6],
                },
              },
            },
            label: {
              color: theme.colors.shark[2],
              fontSize: theme.fontSize.base,
              fontWeight: theme.fontWeight.regular,
            },
            value: {
              backgroundColor: theme.colors.shark[6],
              color: theme.colors.shark[0],
            },
          }),
          variants: {
            "form-control": () => ({
              error: {
                gridColumnStart: 2,
                gridRowStart: 2,
              },
              root: {
                alignItems: "center",
                borderBottom: `1px solid ${theme.colors.shark[7]}`,
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                padding: `${theme.spacing["1.5"]} ${theme.spacing[4]}`,
              },
            }),
          },
        },

        Modal: {
          styles: () => ({
            body: {
              padding: 0,
            },
            content: {
              backgroundColor: theme.colors.shark[8],
              borderRadius: theme.borderRadius.md,
            },
            header: {
              backgroundColor: theme.colors.shark[7],
            },
            overlay: {
              backgroundColor: alpha(theme.colors.fuscousGray["350"], 0.6),
            },
            title: {
              color: theme.colors.white,
              fontFamily: theme.fontFamily.montserrat,
              fontSize: theme.fontSize.xl,
              textAlign: "center",
            },
          }),
        },

        Radio: {
          styles: () => ({
            radio: {
              backgroundColor: theme.colors.transparent,
              borderColor: theme.colors.fuscousGray["300"],

              "&:checked": {
                backgroundColor: theme.colors.transparent,
                borderColor: theme.colors.blueChill["600"],
              },
            },

            icon: {
              color: theme.colors.blueChill[6],
            },

            label: {
              color: theme.colors.shark[0],
              fontSize: theme.fontSize.base,
              fontWeight: theme.fontWeight.regular,
            },
          }),
          variants: {
            "form-control": () => ({
              root: {
                alignItems: "center",
                borderBottom: `1px solid ${theme.colors.shark[7]}`,
                padding: `${theme.spacing[2]} ${theme.spacing[4]}`,
              },
            }),
          },
        },

        Select: {
          defaultProps: {
            size: "md",
            variant: "unstyled",
          },
          sizes: {
            md: () => ({
              input: {
                height: theme.spacing[12],
              },
            }),
          },
          styles: () => ({
            dropdown: {
              background: theme.colors.shark[0],
            },
            input: {
              background: alpha(theme.colors.charade[9], 0.2),
              border: `1px solid ${theme.colors.shark[5]}`,
              borderRadius: theme.borderRadius.md,
              color: theme.colors.shark[0],
              fontSize: theme.fontSize.base,
              fontWeight: theme.fontWeight.regular,
              outline: "none",
              padding: `${theme.spacing[1]} ${theme.spacing[2]}`,
              "&:focus": {
                border: `1px solid ${theme.colors.shark[5]}`,
              },
            },
            item: {
              "&[data-hovered]": {
                backgroundColor: theme.colors.shark[2],
              },
              "&[data-selected]": {
                backgroundColor: theme.colors.shark[5],
                "&:hover": {
                  backgroundColor: theme.colors.shark[6],
                },
              },
            },
            label: {
              color: theme.colors.shark[2],
              fontSize: theme.fontSize.base,
              fontWeight: theme.fontWeight.regular,
            },
          }),
          variants: {
            "form-control": () => ({
              error: {
                gridColumnStart: 2,
                gridRowStart: 2,
              },

              root: {
                alignItems: "center",
                borderBottom: `1px solid ${theme.colors.shark[7]}`,
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                padding: `${theme.spacing["1.5"]} ${theme.spacing[4]}`,
              },
            }),
          },
        },

        Stack: {
          defaultProps: {
            spacing: theme.spacing[0],
          },
        },

        Textarea: {
          defaultProps: {
            variant: "unstyled",
          },
          styles: () => ({
            input: {
              backgroundColor: alpha(theme.colors.shark[8], 0.2),
              border: `1px solid ${theme.colors.shark[5]}`,
              borderRadius: theme.borderRadius.md,
              color: theme.colors.shark[0],
              fontSize: theme.fontSize.sm,
              fontWeight: theme.fontWeight.regular,
              padding: `${theme.spacing[1]} ${theme.spacing[2]}`,
              "&[data-disabled]": {
                background: theme.colors.shark[7],
                color: theme.colors.shark[0],
              },
              "&:focus": {
                border: `1px solid ${theme.colors.shark[5]}`,
              },
            },
            label: {
              color: theme.colors.shark[2],
              fontSize: theme.fontSize.base,
              fontWeight: theme.fontWeight.regular,
            },
          }),
          variants: {
            "form-control": () => ({
              label: {
                textAlign: "center",
                width: "100%",
              },
              root: {
                alignItems: "center",
                borderBottom: `1px solid ${theme.colors.shark[7]}`,
                padding: `${theme.spacing[2]} ${theme.spacing[4]}`,
              },
            }),
          },
        },

        TextInput: {
          defaultProps: {
            size: "md",
            variant: "unstyled",
          },
          sizes: {
            md: () => ({
              input: {
                height: theme.spacing[12],
              },
            }),
          },

          styles: () => ({
            input: {
              background: alpha(theme.colors.charade[9], 0.2),
              border: `1px solid ${theme.colors.shark[5]}`,
              borderRadius: theme.borderRadius.md,
              color: theme.colors.shark[0],
              fontSize: theme.fontSize.base,
              fontWeight: theme.fontWeight.regular,
              outline: "none",
              padding: `${theme.spacing[1]} ${theme.spacing[2]}`,
              "::placeholder, &[data-invalid]::placeholder": {
                color: theme.colors.shark[5],
              },
              "&:focus": {
                border: `1px solid ${theme.colors.shark[5]}`,
              },
              "&:active": {
                border: `1px solid ${theme.colors.shark[5]}`,
              },
            },
            label: {
              color: theme.colors.shark[2],
              fontSize: theme.fontSize.base,
              fontWeight: theme.fontWeight.regular,
            },
          }),
          variants: {
            "form-control": () => ({
              error: {
                gridColumnStart: 2,
                gridRowStart: 2,
              },
              root: {
                alignItems: "center",
                borderBottom: `1px solid ${theme.colors.shark[7]}`,
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                padding: `${theme.spacing["1.5"]} ${theme.spacing[4]}`,
              },
            }),
          },
        },

        ThemeIcon: {
          defaultProps: {
            bg: "none",
          },
        },

        TimeInput: {
          styles: () => ({
            input: {
              background: theme.colors.shark[7],
              border: `1px solid ${theme.colors.shark[5]}`,
              borderRadius: theme.borderRadius.md,
              color: theme.colors.shark[0],
              fontSize: theme.fontSize.base,
              fontWeight: theme.fontWeight.regular,
              outline: "none",
              padding: `${theme.spacing[1]} ${theme.spacing[2]}`,
              "::placeholder": {
                color: theme.colors.shark[5],
              },
            },
            label: {
              color: theme.colors.shark[0],
              fontSize: theme.fontSize.base,
              fontWeight: theme.fontWeight.regular,
            },
          }),
        },

        NumberInput: {
          defaultProps: {
            variant: "unstyled",
          },
          styles: () => ({
            controls: {
              height: "1.125rem",
              width: "1.125rem",
              "&[data-hovered]": {
                backgroundColor: theme.colors.shark[2],
              },
              "&[data-selected]": {
                backgroundColor: theme.colors.shark[5],
                "&:hover": {
                  backgroundColor: theme.colors.shark[6],
                },
              },
            },
            control: {
              background: "none",
              border: "none",
              color: theme.colors.shark[5],
            },
            input: {
              background: theme.colors.shark[7],
              border: `1px solid ${theme.colors.shark[5]}`,
              borderRadius: theme.borderRadius.md,
              color: theme.colors.shark[0],
              fontSize: theme.fontSize.base,
              fontWeight: theme.fontWeight.regular,
              outline: "none",
              padding: `${theme.spacing[1]} ${theme.spacing[2]}`,
            },
            label: {
              color: theme.colors.shark[0],
              fontSize: theme.fontSize.base,
              fontWeight: theme.fontWeight.regular,
            },
          }),
          variants: {
            "form-control": () => ({
              root: {
                alignItems: "center",
                borderBottom: `1px solid ${theme.colors.shark[7]}`,
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                padding: `${theme.spacing[2]} ${theme.spacing[4]}`,
              },
              wrapper: {
                width: "5rem",
              },
            }),
          },
        },
      },

      colors: {
        blackAlpha: colorTuple(theme.colors.blackAlpha),
        blueChill: colorTuple(theme.colors.blueChill),
        bokara: colorTuple(theme.colors.bokara),
        charade: colorTuple(theme.colors.charade),
        chestnutRose: colorTuple(theme.colors.chestnutRose),
        "fuscous-gray": colorTuple(theme.colors.fuscousGray),
        lima: colorTuple(theme.colors.lima),
        roman: colorTuple(theme.colors.roman),
        sandyBrown: colorTuple(theme.colors.sandyBrown),
        shark: colorTuple(theme.colors.shark),
        red: colorTuple(theme.colors.red),
        whiteAlpha: colorTuple(theme.colors.whiteAlpha),
      },

      fontFamily: theme.fontFamily.inter,

      globalStyles: () => ({
        "*, *::before, *::after": {
          boxSizing: "border-box",
        },
      }),
    }}
  >
    <Notifications />
    {children}
  </MantineProvider>
);

export default ThemeProvider;
